<script lang="ts">
  // Svelte / Kit
  import { goto } from '$app/navigation';

  // Ui components
  import * as Form from '$lib/components/ui/form';
  import { Input } from '$lib/components/ui/input/index.js';
  import { LoaderCircle } from '@lucide/svelte';

  // RoastLog
  import { isNewLogin, session } from '$lib/PersistedState.svelte';
  import { initialDataLoad } from '$lib/State.svelte';
  import { login } from '$lib/api';
  import HttpStatusCode from '$lib/types/HttpStatusCode';

  // form stuff
  import { fetchAndStoreInitialDataByLocation, fetchAndStoreUserProfile } from '$lib/dataLoader';
  import { setError, superForm, type Infer, type SuperValidated } from 'sveltekit-superforms';
  import { zodClient } from 'sveltekit-superforms/adapters';
  import { formSchema, type FormSchema } from './schema';

  export let data: SuperValidated<Infer<FormSchema>>;
  export let isLoading = false;

  const form = superForm(data, {
    SPA: true,
    validators: zodClient(formSchema),
    dataType: 'json',
    delayMs: 250,
    timeoutMs: 1500,
    async onUpdate({ form }) {
      if (form.valid) {
        const loginResponse = await login(form.data.username, form.data.password);

        if (loginResponse.status !== HttpStatusCode.OK) {
          let error = '';
          if (loginResponse.response.username) {
            error = loginResponse.response.username[0];
            setError(form, 'username', error);
          } else if (loginResponse.response.password) {
            error = loginResponse.response.password[0];
            setError(form, 'password', error);
          } else if (loginResponse.response.non_field_errors) {
            error = loginResponse.response.non_field_errors[0];
            setError(form, 'password', error);
          }
        } else if (loginResponse.response.token !== undefined) {
          // First, set the API token
          session.token = loginResponse.response.token;
          isNewLogin.current = true;
          initialDataLoad.current = true;

          // Get the user's profile, so that we have their preferred location id
          const userProfile = fetchAndStoreUserProfile();
          // Now, sync the initial data
          fetchAndStoreInitialDataByLocation((await userProfile).location.id);
          // fiinally, redirect to the main page
          await goto('/settings');
        }
      }
    }
  });

  const { form: formData, enhance, delayed } = form;

  delayed.subscribe((val) => {
    isLoading = val;
  });
</script>

<form method="POST" use:enhance>
  <Form.Field {form} name="username">
    <Form.Control>
      {#snippet children({ props })}
        <Form.Label>Username</Form.Label>
        <Input {...props} bind:value={$formData.username} placeholder="user@email.com" />
      {/snippet}
    </Form.Control>
    <Form.FieldErrors />
  </Form.Field>
  <Form.Field {form} name="password">
    <Form.Control>
      {#snippet children({ props })}
        <Form.Label>Password</Form.Label>
        <Input {...props} bind:value={$formData.password} placeholder="******" type="password" />
      {/snippet}
    </Form.Control>
    <Form.FieldErrors />
  </Form.Field>
  {#if isLoading}
    <Form.Button disabled>
      <LoaderCircle class="mr-2 h-4 w-4 animate-spin" />
      Logging you in...
    </Form.Button>
  {:else}
    <Form.Button>Submit</Form.Button>
  {/if}
</form>
